import { useCallback, useMemo, useRef } from "react";

const DEFAULT_IGNORE_KEYS = ["__normalized"];

function stripKeys(value, ignoreKeys) {
  if (value == null || typeof value !== "object") return value;
  if (Array.isArray(value)) {
    return value.map((item) => stripKeys(item, ignoreKeys));
  }
  const out = {};
  for (const [k, v] of Object.entries(value)) {
    if (ignoreKeys.includes(k)) continue;
    out[k] = stripKeys(v, ignoreKeys);
  }
  return out;
}

/** Stabiler JSON-Snapshot für Dirty-Vergleich (ignoriert interne Meta-Keys). */
export function snapshotForm(form, ignoreKeys = DEFAULT_IGNORE_KEYS) {
  if (form == null) return "";
  try {
    return JSON.stringify(stripKeys(form, ignoreKeys));
  } catch {
    return "";
  }
}

export function formsEqual(a, b, ignoreKeys = DEFAULT_IGNORE_KEYS) {
  return snapshotForm(a, ignoreKeys) === snapshotForm(b, ignoreKeys);
}

function cloneForm(form) {
  return JSON.parse(JSON.stringify(form));
}

/**
 * Dirty-State für Formulare: Baseline erst nach Init setzen (syncBaseline / onFormInitialized).
 */
export function useFormDirty(form, options = {}) {
  const { ready = true, ignoreKeys = DEFAULT_IGNORE_KEYS } = options;
  const baselineRef = useRef(null);
  const formRef = useRef(form);
  formRef.current = form;

  const syncBaseline = useCallback((value) => {
    const v = value ?? formRef.current;
    if (v == null) return;
    baselineRef.current = cloneForm(v);
  }, []);

  /** Nach Mount-Effekten (z. B. EntityFormEngine-Normalisierung) Baseline aktualisieren. */
  const scheduleSyncBaseline = useCallback(
    (value) => {
      queueMicrotask(() => {
        syncBaseline(value);
      });
    },
    [syncBaseline]
  );

  const isDirty = useMemo(() => {
    if (!ready || form == null || baselineRef.current == null) return false;
    return !formsEqual(form, baselineRef.current, ignoreKeys);
  }, [form, ready, ignoreKeys]);

  return { isDirty, syncBaseline, scheduleSyncBaseline };
}
